import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Box,
   Typography,
   Avatar,
   Button,
   Chip,
   Divider,
   IconButton,
   Stack,
   Link,
   Tooltip,
   CircularProgress,
   Card,
   CardMedia,
} from '@mui/material';
import {
   Close as CloseIcon,
   Email as EmailIcon,
   LocationOn as LocationIcon,
   Language as LanguageIcon,
   CalendarToday as CalendarIcon,
   Person as PersonIcon,
   Work as WorkIcon,
   BusinessCenter as BusinessCenterIcon,
   Link as LinkIcon,
   Facebook as FacebookIcon,
   Twitter as TwitterIcon,
   Instagram as InstagramIcon,
   LinkedIn as LinkedInIcon,
   ChevronLeft as ChevronLeftIcon,
   ChevronRight as ChevronRightIcon,
   PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { get_user_profile_photos } from '../services';

interface ParticipantProfileData {
   userId?: string | null;
   email: string;
   displayName?: string;
   photoURL?: string;
   profilePicture?: string;
   avatar?: string;
   bio?: string;
   location?: string;
   website?: string;
   title?: string;
   companyName?: string;
   facebook?: string;
   twitter?: string;
   instagram?: string;
   linkedin?: string;
   role?: string;
   joinedAt?: any;
   isInNetwork?: boolean;
   // Additional properties that might exist in different participant types
   status?: string;
   invitedBy?: string;
   invitedAt?: any;
   id?: string;
   profileData?: any;
   // Make the interface more flexible to accept any participant type
   [key: string]: any;
}

interface ParticipantProfileModalProps {
   open: boolean;
   onClose: () => void;
   participant: ParticipantProfileData | null;
   currentUserId?: string | null;
   onAddToNetwork?: (participant: ParticipantProfileData) => void;
   onMessage?: (participant: ParticipantProfileData) => void;
}

const ParticipantProfileModal: React.FC<ParticipantProfileModalProps> = ({
   open,
   onClose,
   participant,
   currentUserId,
   onAddToNetwork,
   onMessage,
}) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   // Photo gallery state
   const [profilePhotos, setProfilePhotos] = useState<any[]>([]);
   const [loadingPhotos, setLoadingPhotos] = useState(false);
   const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
   const [photosError, setPhotosError] = useState<string | null>(null);

   // Fetch profile photos when modal opens and participant changes
   useEffect(() => {
      const fetchProfilePhotos = async () => {
         if (!open || !participant?.userId) {
            if (process.env.NODE_ENV === 'development') {
               console.log(
                  'ParticipantProfileModal: Skipping photo fetch - open:',
                  open,
                  'userId:',
                  participant?.userId
               );
            }
            return;
         }

         if (process.env.NODE_ENV === 'development') {
            console.log('ParticipantProfileModal: Fetching photos for user:', participant.userId);
         }
         setLoadingPhotos(true);
         setPhotosError(null);

         try {
            const photos = await get_user_profile_photos(participant.userId);
            if (process.env.NODE_ENV === 'development') {
               console.log('ParticipantProfileModal: Fetched photos:', photos);
               console.log('ParticipantProfileModal: Photos length:', photos?.length || 0);
            }

            if (photos && photos.length > 0) {
               setProfilePhotos(photos);
               setCurrentPhotoIndex(0);
               if (process.env.NODE_ENV === 'development') {
                  console.log('ParticipantProfileModal: Successfully loaded', photos.length, 'photos');
               }
            } else {
               // No photos found for user - show empty state
               setProfilePhotos([]);
            }
         } catch (error) {
            if (process.env.NODE_ENV === 'development') {
               console.error('ParticipantProfileModal: Error fetching profile photos:', error);
               const errorMessage = error instanceof Error ? error.message : 'Unknown error';
               console.error('ParticipantProfileModal: Error details:', {
                  message: errorMessage,
                  stack: error instanceof Error ? error.stack : undefined,
                  userId: participant.userId,
               });
            }
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setPhotosError(`Failed to load profile photos: ${errorMessage}`);
            setProfilePhotos([]);
         } finally {
            setLoadingPhotos(false);
         }
      };

      fetchProfilePhotos();
   }, [open, participant?.userId]);

   // Photo navigation functions
   const handlePrevPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : profilePhotos.length - 1));
   };

   const handleNextPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev < profilePhotos.length - 1 ? prev + 1 : 0));
   };

   // Early return after all hooks
   if (!participant) return null;

   if (process.env.NODE_ENV === 'development') {
      console.log('Participant data:', participant);
      console.log('Profile data:', participant.profileData);
   }

   const getProfileImage = () => {
      return (
         participant.profileData?.profilePicture ||
         participant.profileData?.photoURL ||
         participant.profileData?.profilePhoto ||
         participant.profileData?.avatar ||
         participant.profilePicture ||
         participant.photoURL ||
         participant.avatar ||
         undefined
      );
   };

   const getDisplayName = () => {
      return (
         participant.profileData?.displayName ||
         participant.profileData?.name ||
         participant.profileData?.fullName ||
         participant.displayName ||
         participant.email?.split('@')[0] ||
         'User'
      );
   };

   const getLocation = () => {
      return participant.profileData?.location || participant.location;
   };

   const getBio = () => {
      return participant.profileData?.bio || participant.bio;
   };

   const getTitle = () => {
      return participant.profileData?.title || participant.title;
   };

   const getCompanyName = () => {
      return participant.profileData?.companyName || participant.companyName;
   };

   const getWebsite = () => {
      return participant.profileData?.website || participant.website;
   };

   const formatDate = (date: any) => {
      if (!date) return 'Unknown';
      try {
         const dateObj = date instanceof Date ? date : new Date(date);
         return dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
         });
      } catch {
         return 'Unknown';
      }
   };

   const socialLinks = [
      {
         key: 'facebook',
         icon: FacebookIcon,
         label: 'Facebook',
         url: participant.profileData?.facebook || participant.facebook,
      },
      {
         key: 'twitter',
         icon: TwitterIcon,
         label: 'Twitter',
         url: participant.profileData?.twitter || participant.twitter,
      },
      {
         key: 'instagram',
         icon: InstagramIcon,
         label: 'Instagram',
         url: participant.profileData?.instagram || participant.instagram,
      },
      {
         key: 'linkedin',
         icon: LinkedInIcon,
         label: 'LinkedIn',
         url: participant.profileData?.linkedin || participant.linkedin,
      },
   ].filter((link) => link.url);

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth='sm'
         fullWidth
         fullScreen={isMobile}
         PaperProps={{
            sx: {
               borderRadius: isMobile ? 0 : 2,
               maxHeight: isMobile ? '100vh' : '90vh',
               minHeight: isMobile ? '100vh' : '600px',
               backgroundColor: 'white',
               boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
         }}
      >
         <DialogTitle
            sx={{
               backgroundColor: 'white',
               borderBottom: '1px solid',
               borderColor: 'divider',
               p: 2,
               position: 'relative',
            }}
         >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
               <IconButton
                  onClick={onClose}
                  size='small'
                  sx={{
                     color: 'text.secondary',
                     '&:hover': {
                        backgroundColor: 'action.hover',
                     },
                  }}
               >
                  <CloseIcon fontSize='small' />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent sx={{ p: 0 }}>
            {/* Hero-style header: gradient background and avatar shadow */}
            <Box
               sx={{
                  background: 'linear-gradient(180deg, rgba(1,148,154,0.06) 0%, transparent 80px)',
                  pt: 2,
                  pb: 3,
                  px: isMobile ? 2 : 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
               }}
            >
               <Avatar
                  src={getProfileImage()}
                  sx={{
                     width: 120,
                     height: 120,
                     mb: 2,
                     border: `3px solid ${theme.palette.primary.main}`,
                     fontSize: '3rem',
                     boxShadow: '0 4px 20px rgba(0, 196, 167, 0.25)',
                  }}
               >
                  {getDisplayName().charAt(0).toUpperCase()}
               </Avatar>

               <Typography
                  variant='h5'
                  fontWeight={600}
                  color='text.primary'
                  textAlign='center'
                  sx={{ mb: 1 }}
               >
                  {getDisplayName()}
               </Typography>

               {participant.role && (
                  <Chip
                     label={participant.role}
                     color={participant.role === 'owner' ? 'primary' : 'secondary'}
                     variant='outlined'
                     size='small'
                     sx={{
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        mb: 1,
                     }}
                  />
               )}

               {participant.email && (
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                        maxWidth: '100%',
                        overflow: 'hidden',
                     }}
                  >
                     <EmailIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap',
                        }}
                     >
                        {participant.email}
                     </Typography>
                  </Box>
               )}
            </Box>

            <Divider sx={{ mb: 0 }} />

            <Box sx={{ p: isMobile ? 2 : 3, mb: 2 }}>

            {/* Profile Information - Only show specific details if available */}
            <Stack spacing={2}>
               {/* Show message if no profile details are available */}
               {!getLocation() &&
                  !getBio() &&
                  !getTitle() &&
                  !getCompanyName() &&
                  !getWebsite() &&
                  socialLinks.length === 0 &&
                  profilePhotos.length === 0 && (
                     <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                           This profile is still being filled out. Connect to stay in touch!
                        </Typography>
                     </Box>
                  )}

               {/* Bio */}
               {getBio() && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                     <PersonIcon color='primary' sx={{ fontSize: 20, mt: 0.5 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                           Bio
                        </Typography>
                        <Box
                           sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              lineHeight: 1.5,
                              '& p': { margin: '0 0 8px 0' },
                              '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
                              '& li': { marginBottom: '4px' },
                              '& strong': { fontWeight: 'bold' },
                              '& em': { fontStyle: 'italic' },
                              '& u': { textDecoration: 'underline' },
                           }}
                           dangerouslySetInnerHTML={{ __html: getBio() }}
                        />
                     </Box>
                  </Box>
               )}

               {/* Job Title */}
               {getTitle() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <WorkIcon color='primary' sx={{ fontSize: 20 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                           Job Title
                        </Typography>
                        <Typography variant='body1' fontWeight={500}>
                           {getTitle()}
                        </Typography>
                     </Box>
                  </Box>
               )}

               {/* Company */}
               {getCompanyName() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <BusinessCenterIcon color='primary' sx={{ fontSize: 20 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                           Company
                        </Typography>
                        <Typography variant='body1' fontWeight={500}>
                           {getCompanyName()}
                        </Typography>
                     </Box>
                  </Box>
               )}

               {/* Social Links */}
               {socialLinks.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                     <LinkIcon color='primary' sx={{ fontSize: 20, mt: 0.5 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                           Social Links
                        </Typography>
                        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                           {socialLinks.map((link) => {
                              const IconComponent = link.icon;
                              return (
                                 <Tooltip key={link.key} title={link.label}>
                                    <IconButton
                                       component={Link}
                                       href={link.url}
                                       target='_blank'
                                       rel='noopener noreferrer'
                                       size='small'
                                       sx={{
                                          color: 'primary.main',
                                          '&:hover': {
                                             backgroundColor: 'primary.main',
                                             color: 'white',
                                          },
                                       }}
                                    >
                                       <IconComponent fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                              );
                           })}
                        </Stack>
                     </Box>
                  </Box>
               )}

               {/* Location */}
               {getLocation() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <LocationIcon color='primary' sx={{ fontSize: 20 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                           Location
                        </Typography>
                        <Typography variant='body1' fontWeight={500}>
                           {getLocation()}
                        </Typography>
                     </Box>
                  </Box>
               )}

               {/* Website Links */}
               {getWebsite() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <LinkIcon color='primary' sx={{ fontSize: 20 }} />
                     <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                           Website
                        </Typography>
                        <Link
                           href={getWebsite()}
                           target='_blank'
                           rel='noopener noreferrer'
                           sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                 textDecoration: 'underline',
                              },
                           }}
                        >
                           <Typography variant='body1' fontWeight={500}>
                              {getWebsite()}
                           </Typography>
                        </Link>
                     </Box>
                  </Box>
               )}

               {/* Photos Section - Last */}
               <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                     <PhotoCameraIcon color='primary' sx={{ fontSize: 20 }} />
                     <Typography variant='body2' color='text.secondary'>
                        Photos
                     </Typography>
                  </Box>

                  <Box sx={{ position: 'relative', width: '100%' }}>
                     {loadingPhotos ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 120,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              backgroundColor: 'grey.50',
                           }}
                        >
                           <CircularProgress />
                        </Box>
                     ) : profilePhotos.length > 0 ? (
                        <Box sx={{ position: 'relative' }}>
                           {/* Photo Scroll Container */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 overflowX: 'auto',
                                 gap: 2,
                                 p: 1,
                                 '&::-webkit-scrollbar': {
                                    height: 6,
                                 },
                                 '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                    borderRadius: 3,
                                 },
                                 '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    borderRadius: 3,
                                    '&:hover': {
                                       backgroundColor: 'rgba(0,0,0,0.5)',
                                    },
                                 },
                              }}
                              id='photo-scroll-container'
                           >
                              {profilePhotos.map((photo, index) => (
                                 <Box
                                    key={photo.id || index}
                                    sx={{
                                       position: 'relative',
                                       flexShrink: 0,
                                       cursor: 'pointer',
                                       transition: 'all 0.2s ease',
                                       '&:hover': {
                                          transform: 'scale(1.05)',
                                       },
                                    }}
                                    onClick={() => setCurrentPhotoIndex(index)}
                                 >
                                    <Card
                                       sx={{
                                          borderRadius: 2,
                                          overflow: 'hidden',
                                          boxShadow: currentPhotoIndex === index ? 4 : 2,
                                          border:
                                             currentPhotoIndex === index
                                                ? `2px solid ${theme.palette.primary.main}`
                                                : 'none',
                                       }}
                                    >
                                       <CardMedia
                                          component='img'
                                          width={80}
                                          height={80}
                                          image={photo.url}
                                          alt={`${getDisplayName()}'s photo ${index + 1}`}
                                          sx={{
                                             objectFit: 'cover',
                                             width: 80,
                                             height: 80,
                                          }}
                                       />
                                    </Card>
                                 </Box>
                              ))}
                           </Box>

                           {/* Navigation Buttons - Always Show */}
                           {profilePhotos.length > 1 && (
                              <>
                                 <IconButton
                                    onClick={() => {
                                       const container =
                                          document.getElementById('photo-scroll-container');
                                       if (container) {
                                          container.scrollLeft -= 100;
                                       }
                                    }}
                                    sx={{
                                       position: 'absolute',
                                       left: -10,
                                       top: '50%',
                                       transform: 'translateY(-50%)',
                                       backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                       color: 'primary.main',
                                       boxShadow: 2,
                                       '&:hover': {
                                          backgroundColor: 'white',
                                          boxShadow: 4,
                                       },
                                       width: 32,
                                       height: 32,
                                    }}
                                 >
                                    <ChevronLeftIcon fontSize='small' />
                                 </IconButton>

                                 <IconButton
                                    onClick={() => {
                                       const container =
                                          document.getElementById('photo-scroll-container');
                                       if (container) {
                                          container.scrollLeft += 100;
                                       }
                                    }}
                                    sx={{
                                       position: 'absolute',
                                       right: -10,
                                       top: '50%',
                                       transform: 'translateY(-50%)',
                                       backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                       color: 'primary.main',
                                       boxShadow: 2,
                                       '&:hover': {
                                          backgroundColor: 'white',
                                          boxShadow: 4,
                                       },
                                       width: 32,
                                       height: 32,
                                    }}
                                 >
                                    <ChevronRightIcon fontSize='small' />
                                 </IconButton>
                              </>
                           )}

                           {/* Photo Counter */}
                           {profilePhotos.length > 1 && (
                              <Typography
                                 variant='caption'
                                 sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    mt: 1,
                                    color: 'text.secondary',
                                 }}
                              >
                                 {profilePhotos.length} photos
                              </Typography>
                           )}
                        </Box>
                     ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                           <Typography variant='body2' color='text.secondary'>
                              No photos shared yet
                           </Typography>
                           <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                              Photos from events can appear here.
                           </Typography>
                        </Box>
                     )}

                     {photosError && (
                        <Typography
                           variant='body2'
                           color='error'
                           sx={{ textAlign: 'center', mt: 1 }}
                        >
                           {photosError}
                        </Typography>
                     )}
                  </Box>
               </Box>
            </Stack>
            </Box>

            {/* Engagement actions */}
            <DialogActions
               sx={{
                  px: isMobile ? 2 : 3,
                  py: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  gap: 1,
               }}
            >
               {participant.userId === currentUserId ? (
                  <Button onClick={onClose} variant='contained'>
                     Close
                  </Button>
               ) : (
                  <>
                     {onAddToNetwork && (
                        <Button
                           variant='contained'
                           onClick={() => onAddToNetwork(participant)}
                           sx={{
                              background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                              boxShadow: '0 4px 15px rgba(0, 196, 167, 0.3)',
                              '&:hover': {
                                 background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                 boxShadow: '0 6px 20px rgba(0, 196, 167, 0.4)',
                              },
                           }}
                        >
                           Add to network
                        </Button>
                     )}
                     {onMessage && (
                        <Button variant='outlined' onClick={() => onMessage(participant)}>
                           Message
                        </Button>
                     )}
                     {(!onAddToNetwork && !onMessage) && (
                        <Button onClick={onClose}>Close</Button>
                     )}
                  </>
               )}
            </DialogActions>
         </DialogContent>
      </Dialog>
   );
};

export default ParticipantProfileModal;
